import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config';

export type RewriteTone = 'concise' | 'friendly' | 'formal' | 'persuasive' | 'neutral';

const buildUrl = (path: string) => {
  const base = API_BASE_URL.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
};

const getToken = async () => {
  const sessionResp = await supabase.auth.getSession();
  const data: any = sessionResp?.data;
  return data?.session?.access_token || '';
};

const fetchJSON = async <T>(url: string, init: RequestInit): Promise<T> => {
  const token = await getToken();
  const res = await fetch(url, {
    ...(init || {}),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(init?.headers || {})
    }
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
};

// New AI Email Writer Types
export interface EmailGenerationRequest {
  purpose: string;
  targetAudience: string;
  companyInfo?: string;
  keyPoints?: string[];
  tone?: 'professional' | 'casual' | 'friendly' | 'direct';
  length?: 'short' | 'medium' | 'long';
  personalization?: {
    recipientRole?: string;
    customContext?: string;
  };
  includeCallToAction?: boolean;
  avoidSpamTriggers?: boolean;
  useSpintax?: boolean;
  spintaxAlternativesPerSentence?: number;
}

export interface EmailVariation {
  subject: string;
  body: string;
  variation_type: string;
  spam_score: number;
}

export interface DeliverabilityAnalysis {
  spam_score: number;
  risk_level: string;
  issues: any[];
  recommendations: string[];
}

export interface EmailGenerationResponse {
  subject: string;
  body: string;
  variations: EmailVariation[];
  deliverability_analysis: DeliverabilityAnalysis;
  best_practices_applied: string[];
  personalization_tokens: string[];
}

export interface EmailImprovementRequest {
  subject: string;
  body: string;
  targetAudience?: string;
  goals?: string[];
}

export interface EmailImprovementResponse {
  improved_subject: string;
  improved_body: string;
  changes_made: string[];
  deliverability_improvement: {
    before_score: number;
    after_score: number;
    improvement_percentage: number;
  };
  conversion_tips: string[];
}

export interface SubjectVariation {
  subject: string;
  spam_score: number;
  strategy: string;
}

const aiEmailService = {
  // Existing functions
  async personalize(template: string, lead: Record<string, any>, campaignContext?: string): Promise<{ text: string }> {
    if (!template) throw new Error('template is required');
    const body = { template, lead, campaignContext };
    return fetchJSON(buildUrl('/api/anthropic/templates/personalize'), {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  async rewrite(draft: string, tone: RewriteTone = 'concise', maxWords = 120): Promise<{ text: string; tone: RewriteTone }> {
    if (!draft) throw new Error('draft is required');
    const body = { draft, tone, maxWords };
    return fetchJSON(buildUrl('/api/anthropic/templates/rewrite'), {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  // New AI Email Writer Functions
  async generateEmail(request: EmailGenerationRequest): Promise<EmailGenerationResponse> {
    const response = await fetchJSON<{ success: boolean; data: EmailGenerationResponse }>(
      buildUrl('/api/email/ai-writer/generate'),
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
    return response.data;
  },

  async improveEmail(request: EmailImprovementRequest): Promise<EmailImprovementResponse> {
    const response = await fetchJSON<{ success: boolean; data: EmailImprovementResponse }>(
      buildUrl('/api/email/ai-writer/improve'),
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
    return response.data;
  },

  async generateSubjectVariations(
    subject: string,
    body: string,
    count: number = 5
  ): Promise<{ original_subject: string; variations: SubjectVariation[]; best_variation: SubjectVariation }> {
    const response = await fetchJSON<{ success: boolean; data: any }>(
      buildUrl('/api/email/ai-writer/subject-variations'),
      {
        method: 'POST',
        body: JSON.stringify({ subject, body, count })
      }
    );
    return response.data;
  },

  async getTemplates(): Promise<any[]> {
    const response = await fetchJSON<{ success: boolean; data: { templates: any[] } }>(
      buildUrl('/api/email/ai-writer/templates'),
      { method: 'GET' }
    );
    return response.data.templates;
  },

  async getBestPractices(): Promise<any> {
    const response = await fetchJSON<{ success: boolean; data: any }>(
      buildUrl('/api/email/ai-writer/best-practices'),
      { method: 'GET' }
    );
    return response.data;
  }
};

export default aiEmailService;
